import React, { useState, createContext, useContext } from "react";
import { usePost } from "seed/api";
import { getPermissionTarget } from "components/util/permissions";

/** @module PermissionsProvider **/

const PermissionsContext = createContext(undefined);

/**
 * Returns the permissions context for the current user
 * @returns {{initializePermissions: Function, hasPermission: Function, isAdmin: boolean, role: Object|null}} Permissions context
 * @throws {Error} When used outside PermissionProvider
 * @example
 * const { initializePermissions, hasPermission } = usePermissions();
 */
const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context)
    throw new Error("usePermissions must be used within a PermissionsProvider");
  return context;
}

/**
 * Provides user permissions to descendant components
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Components that consume the permissions context
 * @returns {React.ReactElement} Permissions context provider
 * @example
 * <PermissionProvider>
 *   <Presentation />
 * </PermissionProvider>
 */
const PermissionProvider = ({ children }) => {

  const [userSnapshot, setUserSnapshot] = useState(null);

  const [callPermissions] = usePost("/users/get_user_permissions", {
    onCompleted: (data) =>
      setUserSnapshot(data)
  });

  /**
   * Fetches the current user's permissions for a flow. Resets the snapshot
   * first so consumers gate correctly while a new flow's permissions load.
   * @param {Object} params Initialization params
   * @param {number|string} params.flowId Flow ID
   * @returns {void}
   */
  const initializePermissions = ({ flowId }) => {
    setUserSnapshot(null);
    callPermissions({ flow_id: flowId });
  };

  /**
   * Checks whether the current user has a permission. The target bucket is
   * resolved from the permission name, so node and collection permissions only
   * need their resource ID, and custom flow permissions work as-is.
   * @param {string} permission Permission name
   * @param {number|string} resourceId Resource ID required for node and collection permissions
   * @param {boolean} defaultValue Value returned before permissions load or when permission is not defined
   * @returns {boolean} Whether the user has the requested permission
   * @throws {Error} When a node or collection permission does not include a resource ID
   * @example
   * hasPermission(PERMISSIONS.NODE_EXECUTE, nodeId, false);
   */
  const hasPermission = (permission, resourceId, defaultValue = false) => {

    if (!userSnapshot)
      return defaultValue;

    if (userSnapshot.is_admin)
      return true;

    const target = getPermissionTarget(permission);

    if (target == "flow")
      return userSnapshot.permissions.flow?.[permission] ?? defaultValue;

    if (!resourceId)
      throw new Error(`Permission ${permission} requires resource ID`);

    return userSnapshot.permissions[target]?.[resourceId]?.[permission] ?? defaultValue;

  }

  return (
    <PermissionsContext.Provider value={{
      initializePermissions,
      hasPermission,
      isAdmin: userSnapshot?.is_admin ?? false,
      role: userSnapshot?.role ?? null,
      snapshot: userSnapshot,
    }}>
      {children}
    </PermissionsContext.Provider>
  );

}


export { PermissionProvider, usePermissions };